import React, { useEffect, useState } from 'react';
import {
  Box, Paper, Typography, Grid, TextField, Button, Tab, Tabs,
} from '@mui/material';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip,
  ResponsiveContainer, PieChart, Pie, Cell, Legend,
} from 'recharts';
import { useReporteStore } from '../store/reporteStore';

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16', '#f97316', '#6366f1', '#14b8a6', '#e11d48'];

interface TabPanelProps { children?: React.ReactNode; index: number; value: number; }
const TabPanel = ({ children, value, index }: TabPanelProps) => (
  <div hidden={value !== index}>{value === index && <Box sx={{ py: 2 }}>{children}</Box>}</div>
);

export const ReportesVentasPage: React.FC = () => {
  const [tab, setTab] = useState(0);
  const {
    margenes, ventasPeriodo, ventasCliente, ventasVendedor,
    filtro, anioSeleccionado, loading,
    fetchMargenes, fetchVentasPeriodo, fetchVentasCliente, fetchVentasVendedor,
    setFiltro, setAnio,
  } = useReporteStore();

  useEffect(() => {
    fetchMargenes();
    fetchVentasPeriodo();
    fetchVentasCliente();
    fetchVentasVendedor();
  }, []);

  const margenColumns: GridColDef[] = [
    { field: 'serie', headerName: 'Serie', width: 80 },
    { field: 'nroDocumento', headerName: 'Numero', width: 100 },
    { field: 'fechaEmision', headerName: 'Fecha', width: 110, valueFormatter: (value: string) => value ? new Date(value).toLocaleDateString('es-PE') : '-' },
    { field: 'razonSocial', headerName: 'Cliente', flex: 1, minWidth: 180 },
    { field: 'totalVenta', headerName: 'Venta', width: 110, type: 'number', valueFormatter: (value: number) => `S/ ${(value ?? 0).toFixed(2)}` },
    { field: 'totalCosto', headerName: 'Costo', width: 110, type: 'number', valueFormatter: (value: number) => `S/ ${(value ?? 0).toFixed(2)}` },
    { field: 'margen', headerName: 'Margen', width: 110, type: 'number', valueFormatter: (value: number) => `S/ ${(value ?? 0).toFixed(2)}` },
    { field: 'porcentajeMargen', headerName: '% Margen', width: 100, type: 'number', valueFormatter: (value: number) => `${(value ?? 0).toFixed(1)}%` },
  ];

  const clienteColumns: GridColDef[] = [
    { field: 'razonSocial', headerName: 'Cliente', flex: 1, minWidth: 200 },
    { field: 'numeroIdentificacion', headerName: 'RUC/DNI', width: 130 },
    { field: 'totalVentas', headerName: 'Total Ventas', width: 130, type: 'number', valueFormatter: (value: number) => `S/ ${(value ?? 0).toFixed(2)}` },
    { field: 'cantidadDocumentos', headerName: 'Documentos', width: 120, type: 'number' },
  ];

  const vendedorColumns: GridColDef[] = [
    { field: 'nombreVendedor', headerName: 'Vendedor', flex: 1, minWidth: 200 },
    { field: 'totalVentas', headerName: 'Total Ventas', width: 130, type: 'number', valueFormatter: (value: number) => `S/ ${(value ?? 0).toFixed(2)}` },
    { field: 'cantidadDocumentos', headerName: 'Documentos', width: 120, type: 'number' },
    { field: 'comisionTotal', headerName: 'Comision', width: 130, type: 'number', valueFormatter: (value: number) => `S/ ${(value ?? 0).toFixed(2)}` },
  ];

  const handleFiltrar = () => {
    fetchMargenes();
    fetchVentasCliente();
    fetchVentasVendedor();
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" fontWeight="bold" sx={{ mb: 3 }}>Reportes de Ventas</Typography>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, sm: 4, md: 3 }}>
            <TextField label="Fecha Desde" size="small" fullWidth type="date" InputLabelProps={{ shrink: true }}
              value={filtro.fechaDesde || ''} onChange={(e) => setFiltro({ fechaDesde: e.target.value || null })} />
          </Grid>
          <Grid size={{ xs: 12, sm: 4, md: 3 }}>
            <TextField label="Fecha Hasta" size="small" fullWidth type="date" InputLabelProps={{ shrink: true }}
              value={filtro.fechaHasta || ''} onChange={(e) => setFiltro({ fechaHasta: e.target.value || null })} />
          </Grid>
          <Grid size={{ xs: 12, sm: 4, md: 2 }}>
            <TextField label="Anio" size="small" fullWidth type="number" value={anioSeleccionado}
              onChange={(e) => { setAnio(parseInt(e.target.value) || new Date().getFullYear()); }} />
          </Grid>
          <Grid size={{ xs: 12, sm: 12, md: 4 }}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button variant="contained" onClick={handleFiltrar}>Filtrar</Button>
              <Button variant="outlined" onClick={() => fetchVentasPeriodo()}>Actualizar Periodo</Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ mb: 2 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)}>
          <Tab label="Ventas por Periodo" />
          <Tab label="Margenes" />
          <Tab label="Por Cliente" />
          <Tab label="Por Vendedor" />
        </Tabs>

        <TabPanel value={tab} index={0}>
          <Box sx={{ p: 2 }}>
            <Box sx={{ height: 350, mb: 2 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={ventasPeriodo}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="nombreMes" />
                  <YAxis />
                  <RechartsTooltip formatter={(value: number) => `S/ ${value.toFixed(2)}`} />
                  <Bar dataKey="totalVentas" fill="#3b82f6" name="Total Ventas" />
                </BarChart>
              </ResponsiveContainer>
            </Box>
            <Grid container spacing={2}>
              {ventasPeriodo.map((vp) => (
                <Grid key={vp.mes} size={{ xs: 6, sm: 4, md: 2 }}>
                  <Paper sx={{ p: 1.5, textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary">{vp.nombreMes}</Typography>
                    <Typography variant="body2" fontWeight="bold">S/ {vp.totalVentas.toFixed(0)}</Typography>
                    <Typography variant="caption" color="text.secondary">{vp.cantidadDocumentos} docs</Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Box>
        </TabPanel>

        <TabPanel value={tab} index={1}>
          <Box sx={{ px: 2 }}>
            <DataGrid rows={margenes} columns={margenColumns} getRowId={(row) => row.idFactura} loading={loading}
              autoHeight pageSizeOptions={[10, 20]} initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
              sx={{ border: 'none' }} disableRowSelectionOnClick />
          </Box>
        </TabPanel>

        <TabPanel value={tab} index={2}>
          <Box sx={{ px: 2 }}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 7 }}>
                <DataGrid rows={ventasCliente} columns={clienteColumns} getRowId={(row) => row.idPersona} loading={loading}
                  autoHeight pageSizeOptions={[10, 20]} initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
                  sx={{ border: 'none' }} disableRowSelectionOnClick />
              </Grid>
              <Grid size={{ xs: 12, md: 5 }}>
                <Box sx={{ height: 350 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={ventasCliente.slice(0, 10)} dataKey="totalVentas" nameKey="razonSocial" cx="50%" cy="50%" outerRadius={120} label>
                        {ventasCliente.slice(0, 10).map((_, index) => (
                          <Cell key={index} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <RechartsTooltip formatter={(value: number) => `S/ ${value.toFixed(2)}`} />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </TabPanel>

        <TabPanel value={tab} index={3}>
          <Box sx={{ px: 2 }}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 7 }}>
                <DataGrid rows={ventasVendedor} columns={vendedorColumns} getRowId={(row) => row.idVendedor} loading={loading}
                  autoHeight pageSizeOptions={[10, 20]} initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
                  sx={{ border: 'none' }} disableRowSelectionOnClick />
              </Grid>
              <Grid size={{ xs: 12, md: 5 }}>
                <Box sx={{ height: 350 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={ventasVendedor} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" />
                      <YAxis dataKey="nombreVendedor" type="category" width={120} />
                      <RechartsTooltip formatter={(value: number) => `S/ ${value.toFixed(2)}`} />
                      <Bar dataKey="totalVentas" fill="#10b981" name="Ventas" />
                      <Bar dataKey="comisionTotal" fill="#f59e0b" name="Comision" />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </TabPanel>
      </Paper>
    </Box>
  );
};
